import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Box,
  Typography,
  Paper,
  IconButton,
  Autocomplete,
  Chip } from
'@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import api from '../../services/api';
import { usePointInTimeFilter } from '../../hooks/usePointInTimeFilter';

const EntityGroupDialog = ({
  open,
  handleClose,
  handleSave,
  currentGroup,
  handleInputChange,
  editGroupMode,
  entityGroups,
  saveButtonDisabled
}) => {
  const { selectedPointInTime, availablePointInTimes } = usePointInTimeFilter();
  const [entities, setEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const level = Number(currentGroup.level);
  const levelIsValid = Number.isInteger(level) && level >= 1;
  const parentGroupRequired = levelIsValid && level > 1;
  const parentSelectionDisabled = level === 1 || !Number.isInteger(level) || level < 1;
  const isDescendantGroup = (group) => {
    const visitedGroupIds = new Set();
    let parentId = group.parentId;

    while (parentId) {
      if (parentId === currentGroup.id) {
        return true;
      }

      if (visitedGroupIds.has(parentId)) {
        return false;
      }

      visitedGroupIds.add(parentId);
      const parentGroup = entityGroups.find((candidateGroup) => candidateGroup.id === parentId);
      parentId = parentGroup?.parentId;
    }

    return false;
  };
  const parentGroupOptions = entityGroups.filter((group) =>
  group.id !== currentGroup.id && Number(group.level) === level - 1 && !isDescendantGroup(group)
  );
  const selectedParentIsAvailable = parentGroupOptions.some((group) => group.id === currentGroup.parentId);
  const parentGroupValue = selectedParentIsAvailable ? currentGroup.parentId : '';
  const parentHelperText = level === 1 ?
    'Level 1 groups do not have parents' :
    parentGroupRequired ?
      `Select a level ${level - 1} parent` :
      'Enter a whole number level greater than or equal to 1';

  // Get PointInTime name for display
  const getPointInTimeName = (pointInTimeCode) => {
    if (!pointInTimeCode) return '';
    const pointInTimeObj = availablePointInTimes.find((pit) => pit.code === pointInTimeCode);
    return pointInTimeObj ? pointInTimeObj.name : pointInTimeCode;
  };

  useEffect(() => {
    const fetchEntities = async () => {
      setLoadingEntities(true);
      try {
        const response = await api.get('/entity-models/master');
        const allEntities = (response.data.data || []).map((entity) => ({
          id: entity.id,
          EntityCode: entity.EntityCode,
          EntityName: entity.EntityName,
          PointInTime: entity.PointInTime,
          label: `${entity.EntityCode} - ${entity.EntityName}`
        }));





        // Determine which point-in-time to filter by
        let filterPointInTime = selectedPointInTime;

        // If scenario filter is 'All', use the group's specific PointInTime
        if (selectedPointInTime === 'All' && currentGroup.PointInTime) {
          filterPointInTime = currentGroup.PointInTime;
        }

        // If no specific point-in-time to filter by, show all entities
        if (!filterPointInTime || filterPointInTime === 'All') {

          setEntities(allEntities);
          return;
        }



        // Filter entities by point-in-time code
        const filteredEntities = allEntities.filter((entity) => {
          const entityMatches = entity.PointInTime === filterPointInTime;
          if (!entityMatches && allEntities.indexOf(entity) < 3) {

          }
          return entityMatches;
        });



        // If no entities match the point-in-time filter, show all entities as fallback
        if (filteredEntities.length === 0) {

          setEntities(allEntities);
        } else {
          setEntities(filteredEntities);
        }
      } catch (error) {
        console.error('Error fetching entities:', error);
      } finally {
        setLoadingEntities(false);
      }
    };

    if (open) {
      fetchEntities();
    }
  }, [open, selectedPointInTime, currentGroup.PointInTime]);
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{editGroupMode ? 'Edit Entity Group' : 'Add New Entity Group'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="name"
              label="Group Name"
              fullWidth
              value={currentGroup.name}
              onChange={handleInputChange}
              required />

          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="description"
              label="Description"
              fullWidth
              value={currentGroup.description}
              onChange={handleInputChange} />

          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Group Type</InputLabel>
              <Select
                name="groupType"
                value={currentGroup.groupType || 'custom'}
                onChange={handleInputChange}
                label="Group Type"
                required>

                <MenuItem value="custom">Custom</MenuItem>
                <MenuItem value="system">System</MenuItem>
                <MenuItem value="reporting">Reporting</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                name="type"
                value={currentGroup.type || 'user_defined'}
                onChange={handleInputChange}
                label="Type"
                required>

                <MenuItem value="user_defined">User Defined</MenuItem>
                <MenuItem value="system_defined">System Defined</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="level"
              label="Level"
              type="number"
              fullWidth
              value={currentGroup.level}
              onChange={handleInputChange}
              inputProps={{ min: 1, step: 1 }}
              helperText="Level 1 is the root level"
              required />

          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required={parentGroupRequired} disabled={parentSelectionDisabled}>
              <InputLabel>Parent Group</InputLabel>
              <Select
                name="parentId"
                value={parentSelectionDisabled ? '' : parentGroupValue}
                onChange={handleInputChange}
                label="Parent Group"
                required={parentGroupRequired}>

                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {parentGroupOptions.map((group) =>
                <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                )}
              </Select>
              <FormHelperText>
                {parentHelperText}
              </FormHelperText>
            </FormControl>
          </Grid>
          {/* Point in Time Selection/Display */}
          <Grid item xs={12}>
            {selectedPointInTime === 'All' ?
            // Show dropdown when filter is 'All'
            <FormControl fullWidth>
                <InputLabel>Point in Time</InputLabel>
                <Select
                name="PointInTime"
                value={currentGroup.PointInTime || ''}
                onChange={handleInputChange}
                label="Point in Time"
                required>

                  {availablePointInTimes.map((pit) =>
                <MenuItem key={pit.code} value={pit.code}>
                      {pit.name}
                    </MenuItem>
                )}
                </Select>
              </FormControl> :

            // Show display chip when specific point in time is selected
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Point in Time:
                  </Typography>
                  <Chip
                  label={(() => {
                    const pointInTimeObj = availablePointInTimes.find((pit) => pit.code === selectedPointInTime);
                    return pointInTimeObj ? pointInTimeObj.name : selectedPointInTime;
                  })()}
                  variant="outlined"
                  color="primary"
                  size="small" />

                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Entity groups are created for the selected point in time. Change the point in time filter in the top bar to create groups for different periods.
                </Typography>
              </Box>
            }
          </Grid>
          {/* Entity List Section (Edit Mode Only) */}
          {editGroupMode &&
          <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Associated Entities
                </Typography>
                {/* Show current entities as chips */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {currentGroup.entityList?.map((entityId) =>
                <Paper key={entityId} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5 }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        {entities.find((e) => e.id === entityId)?.EntityName}
                      </Typography>
                      <IconButton size="small" color="error" onClick={() => {
                    const newEntityList = currentGroup.entityList.filter((id) => id !== entityId);
                    handleInputChange({ target: { name: 'entityList', value: newEntityList } });
                  }}>
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                )}
                </Box>
                {/* Add new entity */}
                <Grid container spacing={1} alignItems="center">
                  <Grid item>
                    <Autocomplete
                    size="small"
                    options={entities.filter((entity) => !currentGroup.entityList?.includes(entity.id))}
                    getOptionLabel={(entity) => entity.EntityName || entity.EntityCode || 'Unknown Entity'}
                    value={selectedEntity}
                    onChange={(event, newValue) => {
                      setSelectedEntity(newValue);
                      handleInputChange({ target: { name: 'entityIdError', value: '' } });
                    }}
                    renderInput={(params) =>
                    <TextField
                      {...params}
                      label="Select Entity"
                      size="small"
                      variant="outlined"
                      sx={{
                        width: '300px',
                        '& .MuiOutlinedInput-root': {
                          minWidth: '300px'
                        }
                      }} />

                    }
                    loading={loadingEntities}
                    disabled={loadingEntities} />

                  </Grid>
                  <Grid item>
                    <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      if (!selectedEntity) return;

                      const entityId = selectedEntity.id;
                      if (currentGroup.entityList?.includes(entityId)) {
                        handleInputChange({ target: { name: 'entityIdError', value: 'Entity already added.' } });
                        return;
                      }

                      // Update entity list
                      const newEntityList = [...(currentGroup.entityList || []), entityId];
                      handleInputChange({
                        target: {
                          name: 'entityList',
                          value: newEntityList
                        }
                      });

                      // Clear error and selection
                      handleInputChange({ target: { name: 'entityIdError', value: '' } });
                      setSelectedEntity(null);
                    }}
                    disabled={!selectedEntity}
                    sx={{
                      height: '40px'
                    }}>

                      Add
                    </Button>
                  </Grid>
                  <Grid item>
                    {currentGroup.entityIdError &&
                  <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                        {currentGroup.entityIdError}
                      </Typography>
                  }
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          }

        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="primary"
          variant="text"
          sx={{
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}>

          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saveButtonDisabled}
          sx={{
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'primary.dark'
            },
            '&.Mui-disabled': {
              backgroundColor: 'action.disabledBackground',
              color: 'action.disabled'
            }
          }}>

          Save
        </Button>
      </DialogActions>
    </Dialog>);

};

export default EntityGroupDialog;
